import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './auth.service';
import { DialogService } from './dialog.service';
import { UserService } from './user.service';
import { WHATS_NEW } from '../components/whats-new/whats-new-entries';
import { WhatsNewDialogComponent } from '../components/whats-new/whats-new-dialog.component';

/**
 * Shows each month's What's New once per person. The newest month a user has opened is saved
 * on their account (whats_new_seen, YYYY-MM) so it follows them across computers, with a
 * per-user browser copy so it never waits on the server.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private static readonly CACHE_KEY = 'whatsNewSeen:';
  // uid already checked this session, so the popup is considered at most once per sign-in.
  private checkedUid: string | null = null;

  constructor(
    private _authService: AuthService,
    private _dialogService: DialogService,
    private _dialog: MatDialog,
    private _userService: UserService
  ) {}

  /** Opens the What's New dialog and counts the newest month as seen. */
  open(): void {
    this._dialogService.openDialog(WhatsNewDialogComponent, 'defaultLargeWithData');
    this.markSeen();
  }

  /** Called after each navigation: opens the dialog once if there's a month this user hasn't seen. */
  maybeShowLatest(url: string): void {
    const uid = String(this._authService.current_user?.['uid'] ?? '');
    if (!this._authService.is_user_authenticated() || !uid || uid === this.checkedUid) {
      return;
    }
    if (url.startsWith('/auth') || url.startsWith('/widgets') || url.startsWith('/whats-new')) {
      return;
    }
    this.checkedUid = uid;

    const latest = this.latestMonth();
    if (!latest || this.seenMonth() >= latest) {
      return;
    }
    // New accounts start caught up, so a new hire isn't greeted with changes from before they joined.
    const created = Number(this._authService.current_user?.['created']) || 0;
    if (created && new Date(created * 1000) >= this.monthStart(WHATS_NEW[0].month)) {
      this.markSeen();
      return;
    }

    // Let the page settle first, and never stack on top of a dialog someone already has open.
    setTimeout(() => {
      if (this._dialog.openDialogs.length > 0) {
        this.checkedUid = null; // try again on the next page instead
        return;
      }
      this.open();
    }, 2000);
  }

  /** Records the newest month as seen, locally and on the account. */
  markSeen(): void {
    const latest = this.latestMonth();
    const uid = this._authService.current_user?.['uid'];
    if (!latest || !uid || this.seenMonth() >= latest) {
      return;
    }
    try {
      localStorage.setItem(WhatsNewService.CACHE_KEY + uid, latest);
    } catch {}
    this._authService.current_user['whats_new_seen'] = latest;
    this._userService.saveWhatsNewSeen(latest).subscribe({ error: () => {} });
  }

  /**
   * Key for the newest update: its month, or 'YYYY-MM-final' when it is the month's second
   * release (the end-of-month update above a mid-month one). The final key sorts after the
   * mid-month one (and before next month's), so people who saw the mid-month update see it
   * again. Labels only set the heading and play no part here.
   */
  private latestMonth(): string {
    const latest = WHATS_NEW[0];
    if (!latest) {
      return '';
    }
    return WHATS_NEW[1]?.month === latest.month ? `${latest.month}-final` : latest.month;
  }

  // The later of the account value and this browser's copy ('' when neither exists).
  private seenMonth(): string {
    const fromAccount = this._authService.current_user?.['whats_new_seen'] || '';
    let cached = '';
    try {
      cached = localStorage.getItem(WhatsNewService.CACHE_KEY + this._authService.current_user?.['uid']) || '';
    } catch {}
    return fromAccount > cached ? fromAccount : cached;
  }

  private monthStart(value: string): Date {
    const [year, month] = value.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }
}
